/*
 * Seaszn: site-wide music player bar.
 *
 * Shopify renders a fresh page (and a fresh <audio> element) on every
 * navigation, so "site-wide continuous playback" is approximated rather than
 * truly seamless:
 *  - The song autoplays on a visitor's very first page view, and playback
 *    position, play/pause state, and mute/volume are then saved to
 *    localStorage on every change.
 *  - On the next page load, that state is restored onto the new <audio>
 *    element, and playback resumes automatically unless the visitor had
 *    explicitly paused it themselves.
 *  - Browsers block audio from auto-starting/resuming without a fresh user
 *    gesture on the page. When that happens, this quietly retries once on
 *    the visitor's very next click/tap/keypress anywhere on the page, so the
 *    song starts as soon as they interact with the page at all, rather than
 *    requiring them to find and press the player's play button.
 *  - Closing the player hides it for the rest of that browser tab's session
 *    (sessionStorage), so it stays out of the way once dismissed.
 *  - Just two controls: play/pause and close. No title, no mute button, and
 *    no seek/progress bar, so the track's length is never shown.
 */
if (!customElements.get('seaszn-music-player')) {
  customElements.define(
    'seaszn-music-player',
    class SeasznMusicPlayer extends HTMLElement {
      static STORAGE_KEY = 'seaszn-music-player-state';
      static DISMISS_KEY = 'seaszn-music-player-dismissed';

      connectedCallback() {
        let dismissed = false;
        try {
          dismissed = sessionStorage.getItem(this.constructor.DISMISS_KEY) === '1';
        } catch (error) {
          dismissed = false;
        }

        if (dismissed) {
          this.hidden = true;
          document.body.classList.remove('has-music-player');
          return;
        }

        this.audio = this.querySelector('audio');
        this.toggleButton = this.querySelector('.seaszn-music-player__toggle');
        this.closeButton = this.querySelector('.seaszn-music-player__close');
        this.playIcon = this.querySelector('.seaszn-music-player__icon--play');
        this.pauseIcon = this.querySelector('.seaszn-music-player__icon--pause');

        if (!this.audio) return;

        this.src = this.getAttribute('data-src') || '';
        this.pendingResume = false;
        this.lastSave = 0;

        const saved = this.readState();
        const restoredTime = saved && saved.src === this.src ? saved.time || 0 : 0;
        // No saved state yet (a visitor's first page view) defaults to
        // autoplay; once they've explicitly paused it, that choice is
        // respected on later page loads instead of forcing it back on.
        const wasPlaying = saved && saved.src === this.src ? !!saved.playing : true;

        this.audio.volume = saved && typeof saved.volume === 'number' ? saved.volume : 1;

        this.audio.addEventListener('loadedmetadata', () => {
          if (restoredTime > 0 && restoredTime < this.audio.duration) {
            this.audio.currentTime = restoredTime;
          }
        });

        this.audio.addEventListener('play', () => {
          this.setPlayingIcon(true);
          this.saveState(true);
        });

        this.audio.addEventListener('pause', () => {
          this.setPlayingIcon(false);
          this.saveState(true);
        });

        this.audio.addEventListener('ended', () => {
          if (!this.audio.loop) this.setPlayingIcon(false);
          this.saveState(true);
        });

        this.audio.addEventListener('timeupdate', () => {
          this.saveState(false);
        });

        this.toggleButton.addEventListener('click', () => {
          if (this.audio.paused) {
            this.attemptPlay();
          } else {
            this.audio.pause();
          }
        });

        this.closeButton.addEventListener('click', () => {
          this.audio.pause();
          try {
            sessionStorage.setItem(this.constructor.DISMISS_KEY, '1');
          } catch (error) {
            /* ignore */
          }
          this.hidden = true;
          document.body.classList.remove('has-music-player');
        });

        window.addEventListener('pagehide', () => this.saveState(true));
        window.addEventListener('beforeunload', () => this.saveState(true));

        if (wasPlaying) {
          this.attemptPlay();
        }
      }

      attemptPlay() {
        const playPromise = this.audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise
            .then(() => {
              this.pendingResume = false;
            })
            .catch(() => {
              this.pendingResume = true;
              this.armResumeOnInteraction();
            });
        }
      }

      armResumeOnInteraction() {
        if (this.resumeArmed) return;
        this.resumeArmed = true;
        const resume = () => {
          if (this.pendingResume && this.audio.paused) {
            this.attemptPlay();
          }
        };
        ['pointerdown', 'keydown'].forEach((eventName) => {
          document.addEventListener(eventName, resume, { once: true, passive: true });
        });
      }

      setPlayingIcon(isPlaying) {
        if (this.playIcon) this.playIcon.hidden = isPlaying;
        if (this.pauseIcon) this.pauseIcon.hidden = !isPlaying;
        this.toggleButton.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
      }

      readState() {
        try {
          const raw = localStorage.getItem(this.constructor.STORAGE_KEY);
          return raw ? JSON.parse(raw) : null;
        } catch (error) {
          return null;
        }
      }

      saveState(force) {
        const now = Date.now();
        if (!force && now - this.lastSave < 1000) return;
        this.lastSave = now;
        try {
          localStorage.setItem(
            this.constructor.STORAGE_KEY,
            JSON.stringify({
              src: this.src,
              time: this.audio.currentTime || 0,
              playing: !this.audio.paused,
              muted: this.audio.muted,
              volume: this.audio.volume,
            })
          );
        } catch (error) {
          /* ignore (e.g. private browsing storage limits) */
        }
      }
    }
  );
}
